/* @jsxImportSource react */
'use client';

import { useCallback, useMemo } from 'react';
import * as React from 'react';

import { type ComponentRenderFn, type HTMLProps } from '@base-ui/react';
import { Field as FieldBase } from '@base-ui/react/field';

import { type AnalyticsProps } from '../../helpers/analytics';
import { useImpressionRef } from '../../helpers/use-impression-ref';
import {
  type AccessibleNameProps,
  hasAccessibleName,
  useAccessibleNameWarning,
} from '../../utils/accessible-name';
import { cn } from '../../utils/cn';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';
import { ATOMS_INPUT_STYLING, inputVariants } from './input';

const textAreaPaddingBySize = {
  xs: 'py-2',
  sm: 'py-4',
  base: 'py-5',
  lg: 'py-6',
} as const;

/**
 * Multi-line text field (renders a native `<textarea>`) sharing the Input
 * visual language. Use for longer free-form input such as notes or messages.
 * It reuses the Input `size` variants and `error` ring styling but grows in
 * height rather than locking to a fixed one.
 *
 * Beyond native textarea attributes (except `size` and `required`), it adds an
 * `onValueChange` convenience callback that receives the current string and
 * fires alongside the standard `onChange`.
 *
 * @example
 * ```tsx
 * <TextArea aria-label="Notes" placeholder="Add a note..." rows={4} />
 * <TextArea aria-label="Message" size="lg" onValueChange={setMessage} />
 * <TextArea aria-label="Bio" error />
 * ```
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>((props, ref) => {
  const {
    className,
    onValueChange,
    size = 'base',
    error = false,
    onChange,
    rows,
    analyticsName,
    trackImpression,
    id,
    name,
    disabled,
    ...inputProps
  } = props;

  const textareaStyles = useMemo(
    () => ({ minHeight: ATOMS_INPUT_STYLING.dimensions[size].height }),
    [size],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(event);
      onValueChange?.(event.target.value);
    },
    [onChange, onValueChange],
  );

  const resolvedRows = rows ?? 1;

  // View events only — typed values are PII and never emitted.
  const viewRef = useImpressionRef<HTMLTextAreaElement>(
    Boolean(analyticsName && trackImpression),
    analyticsName,
    'inputViewEvent',
    ref,
  );

  useAccessibleNameWarning('TextArea', hasAccessibleName({ ...props, id }));

  // Render the native <textarea> through Base UI's Field.Control so that, inside a
  // `Field`/`Form`, it joins the field context and automatically receives
  // `aria-describedby` (description + error) and `aria-invalid` — the same wiring
  // Base UI's Input gets for free, and which a bare <textarea> never would. Outside
  // a Field, Field.Control degrades to a plain control (its context is optional).
  //
  // Function-form `render` (vs inline JSX) keeps the prop a stable callback per the
  // react-perf rules. `renderProps` carries Base UI's field wiring (id, name,
  // aria-describedby, aria-invalid); spread it first, then layer the
  // consumer/styling props on top. `handleChange` replaces Base UI's native
  // onChange (we don't use its dirty/filled tracking — TextArea styling keys off
  // focus/error only, and the value is driven by the consumer/Form).
  const renderTextArea = useCallback<ComponentRenderFn<HTMLProps, FieldBase.Control.State>>(
    (renderProps) => (
      <textarea
        {...renderProps}
        {...inputProps}
        className={cn(
          inputVariants({ size, error, focusIndicator: true }),
          'h-auto', // Input size always sets a fixed height, but it does not apply for textarea
          textAreaPaddingBySize[size],
          className,
        )}
        rows={resolvedRows}
        style={textareaStyles}
        onChange={handleChange}
      />
    ),
    [inputProps, size, error, className, resolvedRows, textareaStyles, handleChange],
  );

  return (
    <FieldBase.Control
      ref={viewRef}
      id={id}
      name={name}
      disabled={disabled}
      render={renderTextArea}
    />
  );
});

TextArea.displayName = 'TextArea';

export type TextAreaProps = {
  onValueChange?: (value: string) => void;
  size?: 'xs' | 'sm' | 'base' | 'lg';
  /** Whether the textarea is in an error state. Applies error ring styling. */
  error?: boolean;
  children?: React.ReactNode;
  className?: string;
} & AnalyticsProps &
  AccessibleNameProps &
  StripUndesiredProps<
    Omit<
      React.TextareaHTMLAttributes<HTMLTextAreaElement>,
      'size' | 'required' | 'aria-label' | 'aria-labelledby'
    >
  >;
